<script setup>
  import { useI18n } from "vue-i18n";
  import { useCalculator } from "@/composables/useCalculator.js";
  import moment from "moment-jalaali";
  import { Menu } from "@/base-components/Headless";
  import Lucide from "@/base-components/Lucide";
  import {
    getServiceStatus,
    getServiceStatusColor,
    getVmIcon,
    getVmStatus,
  } from "@/utils/variables";
  import ConfirmationDialog from "@/base-components/Dialog/ConfirmationDialog.vue";
  import { ref } from "vue";
  import { useNumberFormat } from "@/composables/useNumberFormat.js";
  import ReverseTrafficDialog from "./ReverseTrafficDialog.vue";
  import FlavorListDialog from "./FlavorListDialog.vue";
  import InterfacePanel from "./InterfacePanel.vue";
  import PasswordDialogue from "./PasswordDialogue.vue";

  moment.loadPersian({ usePersianDigits: true });

  const props = defineProps({
    data: {
      type: Object,
      default: null,
    },
    role: {
      type: String,
      default: "",
    },
  });

  const emit = defineEmits([
    "action",
    "changeIp",
    "buyTraffic",
    "flavorUpdate",
    "rebuild",
    "deleteVm",
    "activeRequest",
  ]);
  const { t: $t } = useI18n();
  const liveStatusClasses = {
    ACTIVE: "bg-green-100 text-green-700",
    INACTIVE: "bg-red-100 text-red-700",
    UNKNOWN: "bg-gray-100 text-gray-700",
  };
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  const instance = props.data;
  const actions = ["stop", "start", "reboot_hard", "reboot_soft"];
  const menuItems = actions.map((action) => ({
    title: action.replace(/_/g, "-"),
    value: action,
  }));

  function callAction(action) {
    emit("action", action);
  }

  const currentInterface = ref(null);
  const showChangeIPDialog = ref(false);

  function onCloseChangeIpDialog() {
    showChangeIPDialog.value = false;
  }

  function onConfirmChangeIpDialog() {
    showChangeIPDialog.value = false;
    if (currentInterface.value) emit("changeIp", currentInterface.value);
  }

  function changeIp(iface) {
    currentInterface.value = null;
    if (iface.status === 5 && iface.buildStatus === 2)
      currentInterface.value = iface;
    showChangeIPDialog.value = true;
  }

  const billings = {
    CPU: 0,
    RAM: 0,
    Volume: 0,
  };
  const formatNumber = useNumberFormat().formatNumber;
  Object.keys(billings).forEach((key) => {
    billings[key] = instance.IaasAssetBilling.find((x) => x.name === key) || {
      amount: 0,
      dueAt: null,
    };
  });

  const isReverseDialogVisible = ref(false);
  const reverseIface = ref(null);
  const instanceRegion = ref(null);

  function openTrafficDialog(iface) {
    reverseIface.value = iface;
    instanceRegion.value = props.data.Region;
    isReverseDialogVisible.value = true;
  }

  const isDeleteDialogOpen = ref(false);
  const isFlavorDialogOpen = ref(false);
  const isRebuildDialogOpen = ref(false);

  const passwordDialogue = ref(false);
</script>

<template>
  <FlavorListDialog
    :instance="props.data"
    :is-open="isFlavorDialogOpen"
    v-if="isFlavorDialogOpen"
    @close="isFlavorDialogOpen = false"
    @accept="(flavor) => emit('flavorUpdate', flavor)"
  />
  <ReverseTrafficDialog
    v-if="isReverseDialogVisible"
    :iface="reverseIface"
    :is-open="isReverseDialogVisible"
    @close="isReverseDialogVisible = false"
    @accept="(iface, plan) => emit('buyTraffic', iface, plan)"
  />
  <PasswordDialogue
    v-if="passwordDialogue"
    :is-open="passwordDialogue"
    :pass="instance.InstanceSecret.pass"
    @close="passwordDialogue = false"
  />
  <ConfirmationDialog
    v-if="isDeleteDialogOpen"
    :close="() => (isDeleteDialogOpen = false)"
    :show="isDeleteDialogOpen"
    :accept="() => emit('deleteVm')"
    :deny="() => (isDeleteDialogOpen = false)"
  >
    {{ $t("message.are-you-sure-delete-vm") }}
  </ConfirmationDialog>
  <ConfirmationDialog
    v-if="isRebuildDialogOpen"
    :close="() => (isRebuildDialogOpen = false)"
    :show="isRebuildDialogOpen"
    :accept="() => emit('rebuild')"
    :deny="() => (isRebuildDialogOpen = false)"
  >
    {{ $t("message.are-you-sure-re-build") }}
  </ConfirmationDialog>
  <div v-if="showChangeIPDialog">
    <ConfirmationDialog
      :close="onCloseChangeIpDialog"
      :show="showChangeIPDialog"
      :accept="onConfirmChangeIpDialog"
      :deny="onCloseChangeIpDialog"
    >
      <template v-if="currentInterface">
        {{ $t("message.are-you-sure-reset-ip") }}: {{ currentInterface.ip }}
      </template>
      <template v-else>
        {{ $t("message.you-cannot-reset-ip") }}
      </template>
    </ConfirmationDialog>
  </div>
  <div class="p-6 bg-gray-100 min-h-screen">
    <div class="max-w-5xl mx-auto bg-white shadow rounded-lg p-6">
      <!-- Header -->
      <div class="flex items-center justify-between border-b pb-4">
        <h1 class="text-2xl font-semibold text-gray-700 flex">
          <img
            :src="`/image/icons/flags/${instance.Region.country.toLowerCase()}.svg`"
            class="flag-icon w-8 h-8 me-2"
            alt=""
          />
          <span>
            {{ $t("title.vm-instance") }}
            {{ instance.name }}
          </span>
        </h1>
        <div
          class="flex items-center p-4 cursor-pointer"
          @click="passwordDialogue = true"
        >
          <Lucide icon="Eye" class="w-6 h-6 m-1" />
          <span class="font-semibold">{{ $t("title.showPassword") }}</span>
        </div>
        <div class="flex gap-3">
          <span
            :class="[
              'px-3 py-1 rounded-full text-sm font-medium',
              liveStatusClasses[instance.liveStatus?.status || 'UNKNOWN'],
            ]"
          >
            {{ instance.liveStatus?.status || "Unknown" }}
          </span>
          <Menu>
            <Menu.Button class="relative outline-none block">
              <Lucide icon="Settings" class="w-7 h-7" />
            </Menu.Button>
            <Menu.Items
              class="w-56 mt-px relative bg-primary before:block before:absolute before:bg-black before:inset-0 before:rounded-md before:z-[-1] text-white"
            >
              <Menu.Item
                class="hover:bg-white/5"
                v-for="(menu, index) in menuItems"
                :key="index"
                @click="callAction(menu.value)"
              >
                <Lucide icon="ToggleRight" class="w-4 h-4 me-2" />
                {{ $t(`menu.${menu.title}`) }}
              </Menu.Item>
              <Menu.Item
                class="hover:bg-white/5"
                @click="isRebuildDialogOpen = true"
              >
                <Lucide icon="LucideMonitorCog" class="w-4 h-4 me-2" />
                {{ $t(`menu.rebuild`) }}
              </Menu.Item>
              <Menu.Item
                class="hover:bg-white/5"
                @click="isFlavorDialogOpen = true"
              >
                <Lucide icon="CloudCog" class="w-4 h-4 me-2" />
                {{ $t(`menu.change-flavor`) }}
              </Menu.Item>
              <Menu.Item
                class="hover:bg-red-500 hover:text-white text-red-300"
                @click="isDeleteDialogOpen = true"
              >
                <Lucide icon="Trash" class="w-4 h-4 me-2" />
                {{ $t(`menu.delete-vm`) }}
              </Menu.Item>
            </Menu.Items>
          </Menu>
        </div>
      </div>

      <!-- Instance Details -->
      <div
        class="mt-6 grid col-span-1 xl:col-span-2 md:col-span-2 lg:col-span-2 sm:grid-cols-2 gap-4"
      >
        <div>
          <h2 class="font-semibold text-gray-600">
            {{ $t("title.general-information") }}
          </h2>
          <div
            class="grid grid-cols-2 gap-3 xl:pe-10 md:pe-10 lg:pe-10 sm:pe-10"
          >
            <div>{{ $t("title.id") }}:</div>
            <div class="text-left">
              {{ instance.id }}
            </div>
            <div>{{ $t("title.hostname") }}:</div>
            <div class="text-left">
              {{ instance.hostname }}
            </div>
            <div>{{ $t("title.regions") }}:</div>
            <div class="text-left">
              {{ instance.Region.displayName }}
            </div>
            <div>{{ $t("title.country") }}:</div>
            <div class="text-left">
              {{ instance.Region.country }}
            </div>
            <div>{{ $t("title.created-at") }}:</div>
            <div class="text-left" dir="ltr">
              {{
                moment(instance.createdAt)
                  .locale("fa")
                  .format("jYYYY/jMM/jDD HH:mm:ss")
              }}
            </div>
          </div>
        </div>
        <div>
          <h2 class="font-semibold text-gray-600">
            {{ $t("title.specifications") }}
          </h2>

          <div class="grid grid-cols-3 gap-3">
            <div>{{ $t("title.cpu") }}:</div>
            <div dir="ltr" class="text-end">{{ instance.cpu }} vCPU</div>
            <div>
              {{ formatNumber(billings.CPU.amount) }}
            </div>
            <div>{{ $t("title.ram") }}:</div>
            <div dir="ltr" class="text-end">
              {{ useCalculator(instance.ram) }}
            </div>
            <div>
              {{ formatNumber(billings.RAM.amount) }}
            </div>
            <div>{{ $t("title.disk") }}:</div>
            <div dir="ltr" class="text-end">
              {{ instance.liveStatus?.flavor?.disk }} GB
            </div>
            <div>
              {{ formatNumber(billings.Volume.amount) }}
            </div>
            <div>{{ $t("title.flavors") }}:</div>
            <div dir="ltr" class="text-end">
              {{ instance.liveStatus?.flavor?.original_name }}
            </div>
            <div></div>
            <div>{{ $t("title.due-at") }}:</div>
            <div dir="ltr" class="text-end">
              {{
                moment(billings.CPU.dueAt)
                  .locale("fa")
                  .format("jYYYY/jMM/jDD HH:mm:ss")
              }}
            </div>
            <div></div>
          </div>
        </div>
      </div>

      <!-- Interfaces and Volumes -->
      <div class="mt-6 rounded-2xl border shadow">
        <h2 class="font-semibold text-gray-600 p-3 border-b">
          <Lucide icon="Network" class="inline" />
          {{ $t("title.network-interfaces") }}
        </h2>
        <div class="mt-2 text-gray-700 text-sm space-y-2">
          <InterfacePanel
            v-for="iface in instance.Interfaces"
            :key="iface.id"
            :iface="iface"
            @active-request="emit('activeRequest', $event)"
            @change-ip="changeIp"
            @buy-traffic="openTrafficDialog"
            class="p-3"
            :role="role"
          />
        </div>
      </div>

      <div class="mt-6 border rounded-2xl shadow">
        <h2 class="font-semibold text-gray-600 p-3 border-b">
          <Lucide icon="HardDrive" class="inline" />
          {{ $t("title.volumes") }}
        </h2>

        <div class="mt-2 text-gray-700 text-sm space-y-2">
          <div
            v-for="(volume, index) in instance.Volumes"
            :key="index"
            class="p-5"
          >
            <div class="flex gap-20">
              <div class="flex text-start">
                <span class="font-semibold ml-3">
                  {{ $t("title.name") }}
                </span>
                <span class="ml-2">
                  {{ volume.name || $t("title.pending") }}
                </span>
              </div>
              <div class="flex text-start">
                <span class="font-semibold ml-3">
                  {{ $t("title.size") }}:
                </span>
                <span class="ml-2"> {{ volume.size }} GB </span>
              </div>
              <div class="flex text-start">
                <span class="font-semibold ml-3">
                  {{ $t("title.type") }}
                </span>
                <span class="ml-2">
                  {{ volume.type }}
                </span>
              </div>
              <div class="text-start">
                <Lucide
                  :icon="getVmIcon(volume.buildStatus, volume.powerStatus)[0]"
                  :class="getVmIcon(volume.buildStatus, volume.powerStatus)[1]"
                />
                <span class="mr-1">{{
                  $t(getVmStatus(volume.buildStatus, volume.powerStatus))
                }}</span>
              </div>
              <div class="text-start">
                <Lucide
                  :icon="volume.status !== -1 ? 'Activity' : 'PackageX'"
                  :class="getServiceStatusColor(volume.status)"
                />
                <span class="mr-1">{{
                  $t(getServiceStatus(volume.status))
                }}</span>
              </div>
            </div>
            <div class="text-center my-2 drop-shadow">
              <div class="inline-block border-b w-1/3"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
